import { Link } from "@/components/LLink";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import ContactForm from "@/components/ContactForm";
import { useLang } from "@/i18n";
import SEO from "@/components/SEO";

const LINES = [
  { name: "Porți & Garduri", text: "Batante, culisante sau autoportante, cu panouri pline sau tăiate laser.", href: "/produse/porti-garduri", image: "/images/porti.jpg" },
  { name: "Terase", text: "Terase și pergole metalice, dimensionate pentru spațiul tău.", href: "/produse/terase", image: "/images/terase.jpg" },
  { name: "Carporturi", text: "Adăposturi auto moderne, cu structură calculată și montaj inclus.", href: "/produse/carport", image: "/images/carport.jpg" },
  { name: "Structuri Metalice", text: "Hale, șarpante, console și cadre de rezistență, la cheie.", href: "/produse/structuri-metalice", image: "/images/structuri.jpg" },
  { name: "Mobilier Urban", text: "Bănci, coșuri, suporturi de biciclete și borduri pentru spații publice.", href: "/produse/mobilier-urban", image: "/images/mobilier-urban.jpg" },
  { name: "Lucrări Personalizate", text: "Orice altă structură sau piesă metalică, proiectată la comandă.", href: "/contact", image: "/images/sudura.jpg" },
];

const STEPS = [
  "Debitare laser a componentelor, direct din fișierul tehnic",
  "Îndoire abkant CNC și sudură MIG/TIG/LASER a ansamblului",
  "Vopsire în câmp electrostatic în culoarea RAL aleasă",
  "Control calitate, ambalare și livrare sau montaj la locație",
];

export default function ProductionPage() {
  const { t } = useLang();
  return (
    <div data-testid="productie-page">
      <SEO
        metaKey="productie"
        title="Producție — Porți, Garduri, Terase, Structuri Metalice | HEAVY METAL"
        description="Producție proprie în atelierul din Buzău: porți și garduri metalice, terase, carporturi, structuri metalice, mobilier urban și lucrări personalizate. Flux complet: debitare laser, îndoire, sudură, vopsire electrostatică."
        image="/images/structuri.jpg"
      />
      <PageHero
        kicker={`${t("nav_production")} — Index`}
        title={t("prd_title")}
        subtitle={t("prd_sub")}
        image="/images/structuri.jpg"
      />

      <section className="border-b border-ink/10">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-heading text-3xl font-bold tracking-tighter md:text-5xl">
              {t("prd_lines")}
            </h2>
            <Link to="/contact" data-testid="productie-cta-top" className="gold-underline font-mono text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-ink">
              {t("prd_cta_top")}
            </Link>
          </div>

          <div className="mt-16 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3">
            {LINES.map((l0, i) => { const l = { ...l0, name: t("prd_items")[i][0], text: t("prd_items")[i][1] }; return (
              <Reveal key={l.name} delay={(i % 3) * 0.08} className="bg-base">
                <Link
                  to={l.href}
                  data-testid={`productie-card-${l.href.split("/").pop()}`}
                  className="group block p-6"
                >
                  <div className="overflow-hidden border border-ink/10">
                    <img
                      src={l.image}
                      alt={l.name}
                      className="h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 flex items-baseline justify-between gap-4">
                    <h3 className="font-heading text-[16px] text-ink font-bold tracking-tight">{l.name}</h3>
                    <span className="font-mono text-xs text-ink/35 transition-colors group-hover:text-acid">
                      0{i + 1}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ink/50">{l.text}</p>
                </Link>
              </Reveal>
            ); })}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <p className="kicker">{t("prd_flow_k")}</p>
          <Reveal>
            <h2 className="mt-8 max-w-3xl font-heading text-2xl font-bold leading-tight tracking-tighter md:text-4xl">
              {t("flow_t")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {t("prd_steps").map((step, i) => (
              <Reveal key={step} delay={i * 0.08} className="bg-base">
                <div className="h-full p-8">
                  <span className="font-mono text-sm font-medium text-acid">/0{i + 1}</span>
                  <p className="mt-6 text-sm leading-relaxed text-ink/60">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto grid max-w-7xl gap-16 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="kicker">Contact</p>
            <h2 className="mt-8 font-heading text-3xl font-bold leading-[1.05] tracking-tighter md:text-4xl">
              {t("prd_q")}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink/60">
              {t("prd_qx")}
            </p>
          </div>
          <Reveal delay={0.1}>
            <ContactForm defaultSubject="Altele" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
